import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Select, DatePicker, Input } from "antd";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import { DropDown } from "../../assets/icons";
import PlacesAutocomplete from "react-places-autocomplete";
import {
  CreateProject,
  getClientsData,
  getWorkersData,
  getProjectById,
  updateProject,
} from "../../services/service";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { setFormData, setPage } from "../../redux/feature/AddProjectSlice";
import GoogleMaps from "../../components/common/GoogleMap";
import ButtonLoader from "../../atoms/buttonLoader";
import { Spin } from "antd";
import { toast } from "react-toastify";
import moment from "moment";
import { IMAGE_BASE_URL } from "../../config/api";

const { Option } = Select;
const { TextArea } = Input;

const AddProject = () => {
  const [location, setLocation] = useState("");
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [projectImage, setProjectImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageName, setExistingImageName] = useState("");
  const [imageRemoved, setImageRemoved] = useState(false);
  const fileInputRef = React.useRef(null);
  const navigate = useNavigate();
  const page = useSelector((state) => state.addProject.page);
  const formData = useSelector((state) => state.addProject.formData);
  const { user } = useSelector((state) => state.auth);
  const [selectedLocation, setSelectedLocation] = useState({
    lat: 31.507877,
    lng: 74.336255,
    address: ""
  });
  const locationState = useLocation();
  const { projectId, isEdit } = locationState.state || {};
  const [initializing, setInitializing] = useState(true);

  const dispatch = useDispatch();

  useEffect(() => {
    const handleGetClients = async () => {
      try {
        const response = await getClientsData(1, 100);
        if (response?.success) {
          setClients(response?.data || []);
        }
      } catch (error) {
        console.error("Error fetching clients data:", error);
      }
    };

    const handleGetWorkers = async () => {
      try {
        const response = await getWorkersData("", 1, 500, "");
        if (response?.success) {
          setWorkers(response?.data || []);
        }
      } catch (error) {
        console.error("Error fetching workers data:", error);
      }
    };

    handleGetWorkers();
    handleGetClients();
  }, []);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (projectId && isEdit) {
        setInitializing(true);
        try {
          const response = await getProjectById(projectId);
          console.log('Fetched project data:', response?.data?.project);
          if (response?.data?.project) {
            const project = response.data.project;
            console.log('Project image from API:', project.image);

            formik.setValues({
              projectName: project.title,
              area: project.total_area,
              location: project.address,
              budget: project.budget,
              completionDate: dayjs(project.expectedCompletionDate).format('DD/MM/YYYY'),
              startDate: dayjs(project.expectedStartDate).format('DD/MM/YYYY'),
              description: project.description,
            });
            setSelectedLocation({
              lat: project.latitude,
              lng: project.longitude,
              address: project.address
            });

            // Set existing image if available
            if (project.image) {
              // Ensure the image path doesn't start with a slash to avoid double slashes
              const imagePath = project.image.startsWith('/') ? project.image.substring(1) : project.image;
              const fullImageUrl = project.image.startsWith('http')
                ? project.image
                : `${IMAGE_BASE_URL}${imagePath}`;

              // Extract filename from path
              const fileName = imagePath.split('/').pop();

              console.log('IMAGE_BASE_URL:', IMAGE_BASE_URL);
              console.log('Image path:', imagePath);
              console.log('Full image URL:', fullImageUrl);
              console.log('Extracted filename:', fileName);

              setImagePreview(fullImageUrl);
              setExistingImageName(fileName);
              setImageRemoved(false);
            } else {
              console.log('No image found in project data');
              setImagePreview(null);
              setExistingImageName("");
              setImageRemoved(false);
            }
          }
        } catch (error) {
          console.error("Error fetching project data:", error);
        } finally {
          setInitializing(false);
        }
      } else {
        setInitializing(false);
      }
    };

    fetchProjectData();
  }, [projectId, isEdit]);

  const formik = useFormik({
    initialValues: {
      projectName: "",
      area: "",
      location: "",
      budget: "",
      completionDate: "",
      startDate: "",
      description: "",
    },
    validationSchema: Yup.object().shape({
      projectName: Yup.string().required("Project name is required"),
      area: Yup.number().required("Total area is required"),

      // location: Yup.string().required("Location is required"),
      budget: Yup.number()
        .typeError("Budget must be a number")
        .required("Project budget is required"),
      completionDate: Yup.string().required("Completion date is required"),
      startDate: Yup.string().required("Expected start date is required"),
      description: Yup.string().required("Description is required"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);

      const formatDate = (d) => (d && dayjs(d, "DD/MM/YYYY").isValid())
        ? dayjs(d, "DD/MM/YYYY").format("YYYY-MM-DD")
        : null;

      const formattedStartDate = formatDate(values.startDate);
      const formattedCompletionDate = formatDate(values.completionDate);

      // Always use FormData to handle image upload/removal
      const payload = new FormData();
      const isFormData = true;

      payload.append('title', values.projectName);
      payload.append('address', selectedLocation.address || "");
      payload.append('latitude', selectedLocation.lat);
      payload.append('longitude', selectedLocation.lng);
      payload.append('total_area', Number(values.area));
      if (values.budget !== "" && values.budget !== null && values.budget !== undefined) {
        payload.append('budget', Number(values.budget));
      }
      payload.append('manager_id', parseInt(user?.user?.id));
      payload.append('expectedStartDate', formattedStartDate);
      payload.append('expectedCompletionDate', formattedCompletionDate);
      payload.append('description', values.description);
      payload.append('status', true);

      // Handle image - send new image, empty string if removed, or don't send if unchanged
      if (projectImage) {
        payload.append('image', projectImage);
      } else if (imageRemoved) {
        payload.append('removeImage', 'true');
      }
      try {
        let response;
        if (isEdit) {
          // Update existing project using updateProject instead of patchRequest
          response = await updateProject(projectId, payload, isFormData);
          if (response.success) {
            toast.success("Project updated successfully");
            navigate("/view-project");
          }
        } else {
          // Create new project
          response = await CreateProject(payload, isFormData);
          if (response.success) {
            dispatch(setFormData({
              ...formData,
              projectId: response?.data?.project?.id,
            }));
            dispatch(setPage(page + 1));
          }
        }
      } catch (error) {
        toast.error(error?.response?.data?.message);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleLocationSelect = (selectedLocation) => {
    formik.setFieldValue("location", selectedLocation?.placeName);
    setSelectedLocation({
      lat: selectedLocation.lat,
      lng: selectedLocation.lng,
      address: selectedLocation.placeName
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error("Image size should be less than 5MB");
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error("Please upload a valid image file");
        return;
      }
      setProjectImage(file);
      setImageRemoved(false);
      // Create preview URL for the newly selected image
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setProjectImage(null);
    setImagePreview(null);
    setExistingImageName("");
    setImageRemoved(true);
    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (formData) {
      formik.setFieldValue("projectName", formData?.projectName);
      formik.setFieldValue("area", formData?.area);
      setLocation(formData?.location || "");
      formik.setFieldValue("location", formData?.location);
      formik.setFieldValue("budget", formData?.budget);
      formik.setFieldValue("startDate", formData?.startDate);
      formik.setFieldValue("completionDate", formData?.completionDate);
      formik.setFieldValue("description", formData?.description);
    }
  }, [formData, page]);

  return (
    <>

      {initializing ? (
        <div className="flex justify-center items-center h-[400px]">
          <Spin />
        </div>
      ) : (
        <div className="px-6 pt-6 mt-4 bg-white border border-[#E9E9E9] rounded-md shadow-sm">
          <h2 className="text-[20px] font-semibold text-[#1E3A8A] mb-6">
            Project Details
          </h2>
          <form onSubmit={formik.handleSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <div className="mb-4">
                <Input
                  name="projectName"
                  placeholder="Project name"
                  value={formik.values.projectName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full h-[52px] border ${formik.touched.projectName && formik.errors.projectName
                      ? "border-red-500"
                      : "border-[#E9E9E9]"
                    } rounded-[4px]`}
                />
                {formik.touched.projectName && formik.errors.projectName && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.projectName}
                  </div>
                )}
              </div>
              <div className="mb-4">
                <Input
                  name="area"
                  placeholder="Total estimated area in sqm "
                  type="number"
                  value={formik.values.area}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full h-[52px] border ${formik.touched.area && formik.errors.area
                      ? "border-red-500"
                      : "border-[#E9E9E9]"
                    } rounded-[4px]`}
                />
                {formik.touched.area && formik.errors.area && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.area}
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mb-4">

              <div>
                <Input
                  name="budget"
                  placeholder="Project budget"
                  value={formik.values.budget}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`w-full h-[52px] border ${formik.touched.budget && formik.errors.budget
                      ? "border-red-500"
                      : "border-[#E9E9E9]"
                    } rounded-[4px]`}
                />
                {formik.touched.budget && formik.errors.budget && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.budget}
                  </div>
                )}
              </div>

              <div>
                {projectImage || imagePreview ? (
                  <div className="flex items-center w-full min-h-[52px] p-2 border rounded gap-4 border-[#E9E9E9]">
                    <p className="text-gray-700 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                      {projectImage?.name || existingImageName || "Project image"}
                    </p>
                    <button
                      onClick={handleRemoveImage}
                      type="button"
                      className="shrink-0 text-red-500 hover:text-red-700 font-bold text-xl"
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="projectImage"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="w-full max-w-full h-[52px] px-2 pt-[11px] border rounded gap-4 focus:outline-none border-[#E9E9E9]"
                  />
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mb-4">
              {/* <div>
                <PlacesAutocomplete
                  value={location}
                  onChange={(value) => {
                    setLocation(value);
                    formik.setFieldValue("location", value);
                  }}
                  onSelect={(value) => {
                    setLocation(value);
                    formik.setFieldValue("location", value);
                  }}
                >
                  {({
                    getInputProps,
                    suggestions,
                    getSuggestionItemProps,
                    loading,
                  }) => (
                    <div>
                      <input
                        {...getInputProps({
                          placeholder: "Enter a location",
                          className: `h-[50px] w-full rounded border ${
                            formik.touched.location && formik.errors.location
                              ? "border-red-500"
                              : "border-[#E4E3E4]"
                          } px-3`,
                        })}
                      />
                      <div className="autocomplete-dropdown-container">
                        {loading && <div>Loading...</div>}
                        {suggestions.map((suggestion) => {
                          const className = suggestion.active
                            ? "suggestion-item--active"
                            : "suggestion-item";
                          const style = suggestion.active
                            ? {
                                backgroundColor: "#fafafa",
                                cursor: "pointer",
                              }
                            : {
                                backgroundColor: "#ffffff",
                                cursor: "pointer",
                              };
                          return (
                            <div
                              key={suggestion.placeId}
                              {...getSuggestionItemProps(suggestion, {
                                className,
                                style,
                              })}
                            >
                              <span>{suggestion.description}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </PlacesAutocomplete>
                {formik.touched.location && formik.errors.location && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.location}
                  </div>
                )}
              </div> */}
              <div className="flex flex-col">
                <input
                  type="text"
                  label=""
                  placeholder="Location"
                  width="100%"
                  radius="5px"
                  className="w-full min-h-[52px] px-2 border border-[#E9E9E9] rounded-[4px] disabled:!bg-gray-100 disabled:cursor-not-allowed"
                  name="location"
                  value={formik.values.location}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  // onClick={handleLocationClick}
                  disabled={location.length == 0}
                />
                <div>
                  <GoogleMaps onLocationSelect={handleLocationSelect} />
                </div>
              </div>


            </div>

            <div className="grid md:grid-cols-2 grid-cols-1 gap-4 mb-4">
              <div>
                <DatePicker
                  allowClear={false}
                  disabledDate={(current) => {
                    let customDate = moment().format("YYYY-MM-DD");
                    return current && current < moment(customDate, "YYYY-MM-DD");
                  }}
                  placeholder="Expected start date"
                  name="startDate"
                  className={`h-[50px] border w-full border-[#E9E9E9] rounded-[4px] py-3 focus:outline-none ${formik.touched.startDate && formik.errors.startDate
                      ? "border-red-500"
                      : "border-[#E9E9E9]"
                    }`}
                  onChange={(date) => {
                    const formattedDate = date
                      ? dayjs(date).format("DD/MM/YYYY")
                      : null;
                    formik.setFieldValue("startDate", formattedDate);
                  }}
                  format="DD/MM/YYYY"
                  value={
                    formik.values.startDate
                      ? dayjs(formik.values.startDate, "DD/MM/YYYY")
                      : null
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched.startDate && formik.errors.startDate && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.startDate}
                  </div>
                )}
              </div>

              <div>
                <DatePicker
                  allowClear={false}
                  disabledDate={(current) => {
                    let customDate = moment().format("YYYY-MM-DD");
                    return current && current < moment(customDate, "YYYY-MM-DD");
                  }}
                  placeholder="Estimated completion date"
                  name="completionDate"
                  className={`h-[50px] border w-full border-[#E9E9E9] rounded-[4px] py-3 focus:outline-none ${formik.touched.completionDate && formik.errors.completionDate
                      ? "border-red-500"
                      : "border-[#E9E9E9]"
                    }`}
                  onChange={(date) => {
                    const formattedDate = date
                      ? dayjs(date).format("DD/MM/YYYY")
                      : null;
                    formik.setFieldValue("completionDate", formattedDate);
                  }}
                  format="DD/MM/YYYY"
                  value={
                    formik.values.completionDate
                      ? dayjs(formik.values.completionDate, "DD/MM/YYYY")
                      : null
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched.completionDate &&
                  formik.errors.completionDate && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.completionDate}
                    </div>
                  )}
              </div>
            </div>

            <div className="mb-4">
              <TextArea
                name="description"
                placeholder="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`w-full !h-[156px] border ${formik.touched.description && formik.errors.description
                    ? "border-red-500"
                    : "border-[#E9E9E9]"
                  } rounded-[4px]`}
              />
              {formik.touched.description && formik.errors.description && (
                <div className="text-red-500 text-sm mt-1">
                  {formik.errors.description}
                </div>
              )}
            </div>

            <button
              // onClick={formik.handleSubmit}
              type="submit"
              className="w-[152px] mt-4 mb-[10rem] h-[44px] bg-[#F97316] text-white font-medium text-[20px] rounded-[4px] hover:bg-orange-600"
            >
              {isLoading ? <ButtonLoader /> : isEdit ? "Update Project" : "Add Project"}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AddProject;
